import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { GenericCrudService } from '../../../../../services/generic-crud.service';
import { GenericFormComponent } from '../../../../../shared/components/generic-crud/generic-form/generic-form.component';
import { GenericForm, GenericFormField } from '../../../../../models/GenericCRUD/GenericForm';
import { ModalComponent } from '../../../../../shared/components/modal/modal.component';
import { ConfirmationComponent } from '../../../../../shared/components/confirmation/confirmation.component';
import { CustomDropdownComponent } from '../../../../../shared/components/form-controls/custom-dropdown/custom-dropdown.component';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../../../../environments/environment';

@Component({
  selector: 'app-invoice-details',
  standalone: true,
  imports: [CommonModule, RouterLink, GenericFormComponent, FormsModule, ReactiveFormsModule, ModalComponent, ConfirmationComponent, CustomDropdownComponent],
  templateUrl: './invoice-details.component.html',
  styleUrl: './invoice-details.component.css'
})
export class InvoiceDetailsComponent implements OnInit {
  invoiceId!: number;
  invoice: any = null;
  loading: boolean = true;
  editMode: boolean = false;

  // Historial de auditoría (solo para borradores)
  auditHistory: any[] = [];
  loadingAuditHistory: boolean = false;
  showAuditHistory: boolean = false;

  // Facturas rectificativas
  isCorrectiveInvoice: boolean = false;
  canCreateCorrective: boolean = false;
  canEdit: boolean = false;
  correctiveInvoices: any[] = [];
  readonly rectificationTypes = [
    { id: 'error', name: 'Corrección de error' },
    { id: 'return', name: 'Devolución' },
    { id: 'discount', name: 'Descuento posterior' },
    { id: 'cancellation', name: 'Anulación' },
  ];
  showCorrectiveModal: boolean = false;
  correctiveFormGroup: FormGroup;
  submittingCorrective: boolean = false;
  // Diálogo para crear nueva factura tras rectificativa
  showNewInvoiceDialog: boolean = false;

  // PDF
  downloadingPdf: boolean = false;

  // Modal de confirmación
  showConfirmation: boolean = false;
  confirmationQuestion: string = '';
  confirmationAction: (() => void) | null = null;

  formFieldsEdit: GenericFormField[] = [
    { name: 'invoice_date', type: 'date', label: 'Fecha', validations: 'required', colspan: 1 },
    { name: 'due_date', type: 'date', label: 'Fecha de Vencimiento', validations: '', colspan: 1 },
    { name: 'user_id', type: 'select', label: 'Alumno', url: '/user/getAll', validations: 'required', singleSelection: true, colspan: 1 },
    { name: 'company_id', type: 'select', label: 'Sociedad', url: '/config/company/listAll', validations: '', singleSelection: true, colspan: 1 },
    { name: 'subtotal', type: 'number', label: 'Base Imponible', validations: 'required|min:0', colspan: 1 },
    { name: 'tax_rate_id', type: 'select', label: 'Tipo de IVA', url: '/tax-rate/all?active=true&type=tax', validations: '', singleSelection: true, colspan: 1 },
    { name: 'retention_rate_id', type: 'select', label: 'Retención', url: '/tax-rate/all?active=true&type=retention', validations: '', singleSelection: true, colspan: 2 },
    {
      name: 'payment_method',
      type: 'select',
      label: 'Método de Pago',
      validations: '',
      optionsList: [
        { id: 'cash', name: 'Efectivo' },
        { id: 'transfer', name: 'Transferencia' },
        { id: 'card', name: 'Tarjeta' },
        { id: 'direct_debit', name: 'Domiciliación' },
        { id: 'split', name: 'Fraccionado' },
      { id: 'wallet', name: 'Monedero digital' }
      ],
      singleSelection: true,
      colspan: 1
    },
    {
      name: 'pos_terminal_id',
      type: 'select',
      label: 'TPV',
      url: '/config/pos-terminal/listAll',
      validations: '',
      singleSelection: true,
      colspan: 1,
      showIf: (item?: any) => {
        const pm = item?.payment_method;
        const method = typeof pm === 'object' ? pm?.id : pm;
        return method === 'card' || method === 'split';
      }
    },
    {
      name: 'cash_amount',
      type: 'number',
      label: 'Importe en Efectivo (€)',
      validations: 'min:0',
      colspan: 1,
      showIf: (item?: any) => {
        const pm = item?.payment_method;
        const method = typeof pm === 'object' ? pm?.id : pm;
        return method === 'split';
      },
    },
    {
      name: 'card_amount',
      type: 'number',
      label: 'Importe en Tarjeta (€)',
      validations: 'min:0',
      colspan: 1,
      showIf: (item?: any) => {
        const pm = item?.payment_method;
        const method = typeof pm === 'object' ? pm?.id : pm;
        return method === 'split';
      },
    },
    {
      name: 'status',
      type: 'select',
      label: 'Estado',
      validations: 'required',
      optionsList: [
        { id: 'draft', name: 'Borrador' },
        { id: 'sent', name: 'Enviada' },
        { id: 'paid', name: 'Pagada' },
        { id: 'overdue', name: 'Vencida' },
        { id: 'cancelled', name: 'Anulada' }
      ],
      singleSelection: true,
      colspan: 1
    },
    { name: 'notes', type: 'textarea', label: 'Notas', validations: '', colspan: 2 }
  ];

  editForm!: GenericForm;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private genericCrudService: GenericCrudService,
    private sanitizer: DomSanitizer,
    private fb: FormBuilder,
    private toastr: ToastrService
  ) {
    // Inicializar el formulario vacío para evitar errores de template
    this.correctiveFormGroup = this.fb.group({
      rectification_type: [''],
      rectification_reason: ['']
    });
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.invoiceId = +params['id'];
      this.loadInvoiceDetails();
    });
  }

  loadInvoiceDetails(): void {
    this.loading = true;
    this.genericCrudService.list(`/invoice/show/${this.invoiceId}`).subscribe({
      next: (response) => {
        this.invoice = response.data;

        // Normalizar fechas para evitar problemas de zona horaria
        // Extraer solo la parte de fecha (YYYY-MM-DD) sin hora
        if (this.invoice.invoice_date) {
          this.invoice.invoice_date = this.invoice.invoice_date.split('T')[0];
        }
        if (this.invoice.due_date) {
          this.invoice.due_date = this.invoice.due_date.split('T')[0];
        }
        if (this.invoice.paid_date) {
          this.invoice.paid_date = this.invoice.paid_date.split('T')[0];
        }

        // Establecer flags de rectificativas
        this.isCorrectiveInvoice = this.invoice.is_corrective || false;

        // Cargar facturas rectificativas si las hay
        this.correctiveInvoices = this.invoice.corrective_invoices || [];

        // Solo se puede crear una rectificativa si:
        // 1. No es un borrador
        // 2. No es una factura rectificativa
        // 3. No tiene ya una factura rectificativa creada
        this.canCreateCorrective = this.invoice.status !== 'draft'
                                  && !this.isCorrectiveInvoice
                                  && this.correctiveInvoices.length === 0;
        this.canEdit = this.invoice.status === 'draft' && !this.isCorrectiveInvoice;

        // Solo cargar historial de auditoría para facturas borrador (editables)
        if (this.invoice.status === 'draft') {
          this.loadAuditHistory();
        }

        this.loading = false;
      },
      error: (error: any) => {
        console.error('Error al cargar factura:', error);
        this.loading = false;
      }
    });
  }

  toggleEditMode(): void {
    this.editMode = !this.editMode;
    if (this.editMode) {
      this.editForm = {
        method: 'PUT',
        successTitle: 'Factura editada correctamente',
        errorTitle: 'Error al editar factura',
        fields: this.formFieldsEdit,
        name: 'factura',
        createButtonTitle: '',
        cols: 2,
        createApiSlug: '',
        updateApiSlug: `/invoice/update/${this.invoiceId}`,
        formTitle: 'Editar Factura',
        formButtonTitle: 'Guardar cambios',
        width: '4xl'
      };
    }
  }

  onEditFinished(updatedData: any): void {
    if (updatedData) {
      this.invoice = updatedData;

      // Normalizar fechas en los datos actualizados
      if (this.invoice.invoice_date) {
        this.invoice.invoice_date = this.invoice.invoice_date.split('T')[0];
      }
      if (this.invoice.due_date) {
        this.invoice.due_date = this.invoice.due_date.split('T')[0];
      }
      if (this.invoice.paid_date) {
        this.invoice.paid_date = this.invoice.paid_date.split('T')[0];
      }

      // Solo recargar historial si es borrador
      if (this.invoice.status === 'draft') {
        this.loadAuditHistory();
      }
    } else {
      this.loadInvoiceDetails();
    }

    this.editMode = false;
  }

  getTypeLabel(type: string): string {
    const types: Record<string, string> = {
      'issued': 'Emitida',
      'received': 'Recibida',
      'proforma': 'Proforma'
    };
    return types[type] || type;
  }

  getTypeColor(type: string): string {
    const colors: Record<string, string> = {
      'issued': 'bg-blue-100 text-blue-800',
      'received': 'bg-purple-100 text-purple-800',
      'proforma': 'bg-yellow-100 text-yellow-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  }

  getStatusLabel(status: string): string {
    const statuses: Record<string, string> = {
      'draft': 'Borrador',
      'sent': 'Enviada',
      'paid': 'Pagada',
      'overdue': 'Vencida',
      'cancelled': 'Anulada',
      'rectified': 'Rectificada'
    };
    return statuses[status] || status;
  }

  getStatusColor(status: string): string {
    const colors: Record<string, string> = {
      'draft': 'bg-gray-100 text-gray-800',
      'sent': 'bg-blue-100 text-blue-800',
      'paid': 'bg-green-100 text-green-800',
      'overdue': 'bg-red-100 text-red-800',
      'cancelled': 'bg-red-50 text-red-600',
      'rectified': 'bg-amber-100 text-amber-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  }

  getPaymentMethodLabel(method: string): string {
    const methods: Record<string, string> = {
      'cash': 'Efectivo',
      'transfer': 'Transferencia',
      'card': 'Tarjeta',
      'direct_debit': 'Domiciliación',
      'split': 'Fraccionado',
      'wallet': 'Monedero digital'
    };
    return methods[method] || method;
  }

  // Métodos para historial de auditoría

  toggleAuditHistory(): void {
    this.showAuditHistory = !this.showAuditHistory;
  }

  loadAuditHistory(): void {
    this.loadingAuditHistory = true;
    this.genericCrudService.list(`/invoice/audit-history/${this.invoiceId}`).subscribe({
      next: (response) => {
        this.auditHistory = response.data || [];
        this.loadingAuditHistory = false;
      },
      error: (error: any) => {
        console.error('Error al cargar historial de auditoría:', error);
        this.loadingAuditHistory = false;
      }
    });
  }

  getActionIcon(action: string): string {
    const icons: Record<string, string> = {
      'created': '<svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>',
      'updated': '<svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>',
      'deleted': '<svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>',
      'marked_as_paid': '<svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>',
      'marked_as_sent': '<svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke-linecap="round" stroke-linejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>',
      'created_as_corrective': '<svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>',
    };
    return icons[action] || '<svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>';
  }

  getActionColor(action: string): string {
    const colors: Record<string, string> = {
      'created': 'bg-green-50 border-green-200',
      'updated': 'bg-blue-50 border-blue-200',
      'deleted': 'bg-red-50 border-red-200',
      'marked_as_paid': 'bg-green-50 border-green-200',
      'marked_as_sent': 'bg-blue-50 border-blue-200',
      'created_as_corrective': 'bg-orange-50 border-orange-200'
    };
    return colors[action] || 'bg-gray-50 border-gray-200';
  }

  getSafeActionIcon(action: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.getActionIcon(action));
  }

  // Métodos para facturas rectificativas

  openCorrectiveModal(): void {
    this.correctiveFormGroup = this.fb.group({
      rectification_type: ['', Validators.required],
      rectification_reason: ['', [Validators.required, Validators.minLength(10)]],
    });
    this.showCorrectiveModal = true;
  }

  closeCorrectiveModal(): void {
    this.showCorrectiveModal = false;
    this.correctiveFormGroup.reset();
  }

  onSubmitCorrectiveForm(): void {
    if (this.correctiveFormGroup.invalid) {
      this.toastr.error('Por favor completa todos los campos requeridos', 'Formulario inválido');
      return;
    }

    this.submittingCorrective = true;
    const formData = this.correctiveFormGroup.value;

    this.genericCrudService.create(`/invoice/create-corrective/${this.invoiceId}`, formData).subscribe({
      next: (response) => {
        this.submittingCorrective = false;
        if (response.success) {
          this.toastr.success('Factura rectificativa creada correctamente');
          this.closeCorrectiveModal();
          this.loadInvoiceDetails();
          // Ofrecer crear una nueva factura
          this.showNewInvoiceDialog = true;
        } else {
          const errorMessage = response.message || 'Error al crear la factura rectificativa';
          this.toastr.error(errorMessage, 'Error');
        }
      },
      error: (error: any) => {
        this.submittingCorrective = false;
        const errorMessage = error?.error?.message || 'Error al crear la factura rectificativa';
        this.toastr.error(errorMessage, 'Error');
      }
    });
  }

  // Métodos para el diálogo de crear nueva factura tras rectificativa

  onConfirmCreateNewInvoice(): void {
    this.showNewInvoiceDialog = false;
    const queryParams: any = {
      entity: 'invoices',
      action: 'create',
      prefill_user_id: this.invoice.user_id
    };
    if (this.invoice.company_id) {
      queryParams.prefill_company_id = this.invoice.company_id;
    }
    this.router.navigate(['/dashboard/administracion'], { queryParams });
  }

  onCancelCreateNewInvoice(): void {
    this.showNewInvoiceDialog = false;
  }

  getRectificationTypeLabel(type: string): string {
    const types: Record<string, string> = {
      'error': 'Corrección de error',
      'return': 'Devolución',
      'discount': 'Descuento posterior',
      'cancellation': 'Anulación'
    };
    return types[type] || type;
  }

  getRectificationTypeColor(type: string): string {
    const colors: Record<string, string> = {
      'error': 'bg-yellow-100 text-yellow-800',
      'return': 'bg-red-100 text-red-800',
      'discount': 'bg-green-100 text-green-800',
      'cancellation': 'bg-gray-100 text-gray-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  }

  // Método para mostrar modal de confirmación
  showConfirmationModal(question: string, action: () => void): void {
    this.confirmationQuestion = question;
    this.confirmationAction = action;
    this.showConfirmation = true;
  }

  // Confirmar acción del modal
  onConfirm(): void {
    if (this.confirmationAction) {
      this.confirmationAction();
    }
    this.showConfirmation = false;
    this.confirmationAction = null;
  }

  // Cancelar acción del modal
  onCancelConfirmation(): void {
    this.showConfirmation = false;
    this.confirmationAction = null;
  }

  // Descargar PDF de la factura
  downloadPdf(): void {
    this.downloadingPdf = true;
    this.http.get(`${environment.API_URL}/invoice/generate-pdf/${this.invoiceId}`, {
      responseType: 'blob'
    }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const prefix = this.invoice.is_corrective ? 'rectificativa' : 'factura';
        a.download = `${prefix}_${this.invoice.invoice_number}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        this.downloadingPdf = false;
      },
      error: () => {
        this.toastr.error('Error al generar el PDF');
        this.downloadingPdf = false;
      }
    });
  }

  // Método para marcar factura como enviada
  markAsSent(): void {
    this.showConfirmationModal(
      '¿Estás seguro de que quieres marcar esta factura como enviada?',
      () => {
        this.genericCrudService.create(`/invoice/mark-as-sent/${this.invoiceId}`, {}).subscribe({
          next: (response) => {
            if (response.success) {
              this.toastr.success('Factura marcada como enviada correctamente');
              this.loadInvoiceDetails();
            } else {
              this.toastr.error(response.message || 'Error al marcar la factura como enviada');
            }
          },
          error: (error: any) => {
            const errorMessage = error?.error?.message || 'Error al marcar la factura como enviada';
            this.toastr.error(errorMessage, 'Error');
            console.error('Error al marcar como enviada:', error);
          }
        });
      }
    );
  }

  // Método para marcar factura como pagada
  markAsPaid(): void {
    this.showConfirmationModal(
      '¿Estás seguro de que quieres marcar esta factura como pagada?',
      () => {
        this.genericCrudService.create(`/invoice/mark-as-paid/${this.invoiceId}`, {}).subscribe({
          next: (response) => {
            if (response.success) {
              this.toastr.success('Factura marcada como pagada correctamente');
              this.loadInvoiceDetails();
            } else {
              this.toastr.error(response.message || 'Error al marcar la factura como pagada');
            }
          },
          error: (error: any) => {
            const errorMessage = error?.error?.message || 'Error al marcar la factura como pagada';
            this.toastr.error(errorMessage, 'Error');
            console.error('Error al marcar como pagada:', error);
          }
        });
      }
    );
  }
}
